import React, { useRef, useState } from "react";
import SignaturePad from "react-signature-canvas";
import Swal from "sweetalert2";
import { AddIcon, DeleteIcon } from "../../common/icons";
import { MediaItem } from "./MediaItem";
import { format } from "date-fns";
import nl from "date-fns/locale/nl";
import { CloseButton } from "react-bootstrap";

function Modal({ open, onClose, children }) {
    if (!open) return null;
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center backdrop-blur-sm">
            <div className="bg-white rounded shadow-lg p-4 min-w-[350px] relative">
                <button
                    className="absolute top-2 right-2 text-gray-500"
                    onClick={onClose}
                >
                    <CloseButton />
                </button>
                {children}
            </div>
        </div>
    );
}

export default function GevaarlijkeSituatieSection({
    customerName,
    objectName,
    gebrek,
    gsUploadErrors = {},
    onUpdateGebrek,
    onSetGsUploadError,
    index,
    upload_image,
    report_situatie,
}) {
    const [showSignatureModal, setShowSignatureModal] = useState(false);
    const [signatureError, setSignatureError] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [pendingSituatieIndex, setPendingSituatieIndex] = useState(null);
    const signaturePadRef = useRef(null);

    const hasReportedSituation = gebrek.gevaarlijkesituatie?.some(
        (sit) => !sit.gevaarlijkesituatieid.startsWith("temp-")
    );

    const addGevaarlijkeSituatieInternal = () => {
        if (!gebrek.media || gebrek.media.length === 0) {
            Swal.fire({
                icon: "warning",
                title: "Geen media",
                text: "Voeg eerst media toe aan het gebrek voordat u een gevaarlijke situatie meldt.",
            });
            return;
        }
        const tempId = `temp-gs-${Date.now()}`;
        const now = new Date();
        const formattedDate = now.toISOString().slice(0, 16).replace("T", " ");
        const firstMedia = gebrek.media[0];
        const newSituatie = {
            gevaarlijkesituatieid: tempId,
            datum: formattedDate,
            media: firstMedia.url,
            mediaid: firstMedia.mediaid,
        };
        const updatedSituaties = [
            ...(gebrek.gevaarlijkesituatie || []),
            newSituatie,
        ];
        onUpdateGebrek(index, {
            ...gebrek,
            gevaarlijkesituatie: updatedSituaties,
        });

        // Instantly open the modal for the new situatie
        setPendingSituatieIndex(updatedSituaties.length - 1);
        setShowSignatureModal(true);
        setSignatureError("");
        if (signaturePadRef.current) signaturePadRef.current.clear();
    };



    const deleteGevaarlijkeSituatieInternal = (situatieIdToDelete) => {
        Swal.fire({
            title: "Weet u het zeker?",
            text: "U staat op het punt deze gevaarlijke situatie melding te verwijderen.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#3085d6",
            confirmButtonText: "Ja, verwijder!",
            cancelButtonText: "Annuleren",
            reverseButtons: true,
        }).then((result) => {
            if (result.isConfirmed) {
                const updatedSituaties = (gebrek.gevaarlijkesituatie || []).filter(
                    (gs) => gs.gevaarlijkesituatieid !== situatieIdToDelete
                );
                onUpdateGebrek(index, {
                    ...gebrek,
                    gevaarlijkesituatie: updatedSituaties,
                });
                onSetGsUploadError(situatieIdToDelete, null);
                Swal.fire({
                    title: "Verwijderd!",
                    text: "De melding is verwijderd.",
                    icon: "success",
                    timer: 1000,
                    showCloseButton: true,
                });
            }
        });
    };

    const handleGevaarlijkeSituatieMediaUploadInternal = async (
        situatieIndex,
        situatieId,
        event
    ) => {
        const file = event.target.files?.[0];
        if (!file) return;

        onSetGsUploadError(situatieId, null);

        try {
            const response = await upload_image(file);

            if (response && response.response) {
                const mediaItem = response.response;
                const updatedSituaties = [...(gebrek.gevaarlijkesituatie || [])];
                if (
                    updatedSituaties[situatieIndex] &&
                    updatedSituaties[situatieIndex].gevaarlijkesituatieid === situatieId
                ) {
                    updatedSituaties[situatieIndex] = {
                        ...updatedSituaties[situatieIndex],
                        media: mediaItem.url,
                        mediaid: mediaItem.mediaid,
                    };
                    onUpdateGebrek(index, {
                        ...gebrek,
                        gevaarlijkesituatie: updatedSituaties,
                    });
                } else {
                    console.error("Mismatch in situatie index/ID during update");
                }
            } else {
                throw new Error("Upload response did not contain valid media data.");
            }
        } catch (uploadErr) {
            console.error("GS Media Upload failed:", uploadErr);
            onSetGsUploadError(situatieId, `Upload mislukt: ${uploadErr.message}`);
        } finally {
            event.target.value = null;
        }
    };

    // Handle reporting with signature
    const handleReportWithSignature = async () => {
        if (!signaturePadRef.current || signaturePadRef.current.isEmpty()) {
            setSignatureError("Handtekening is verplicht.");
            return;
        }
        setSignatureError("");
        setIsSubmitting(true);

        const signatureDataUrl = signaturePadRef.current.toDataURL("image/png")

        // Upload signature as media
        try {
            // Convert dataURL to Blob
            const res = await fetch(signatureDataUrl);
            const blob = await res.blob();
            const file = new File([blob], "signature.png", { type: "image/png" });
            const uploadRes = await upload_image(file);

            if (!uploadRes || !uploadRes.response) {
                throw new Error("Kon handtekening niet uploaden.");
            }
            const signatureMediaId = uploadRes.response.mediaid;
            const situatie = gebrek.gevaarlijkesituatie[pendingSituatieIndex];

            if (
                !gebrek?.gebrekid ||
                !situatie ||
                !situatie.mediaid
            ) {
                throw new Error("Benodigde gegevens ontbreken.");
            }

            // Report situatie with signature
            const situatieObject = {
                gebrekid: gebrek.gebrekid,
                signmedia: signatureMediaId,
            };

            Swal.fire({
                title: "Bezig met melden...",
                text: "Een ogenblik geduld.",
                allowOutsideClick: false,
                didOpen: () => {
                    Swal.showLoading();
                },
            });

            const response = await report_situatie(situatieObject);

            if (response && response.ok) {
                Swal.fire({
                    icon: "success",
                    title: "Gemeld!",
                    text: "De gevaarlijke situatie is succesvol gemeld.",
                    timer: 2000,
                    timerProgressBar: true,
                });
                // Update the local state to reflect the change from temp ID
                const updatedSituaties = gebrek.gevaarlijkesituatie.map((sit, idx) =>
                    idx === pendingSituatieIndex
                        ? {
                            ...sit,
                            gevaarlijkesituatieid: response.newId || "reported",
                            signature: uploadRes.response.url,
                        }
                        : sit
                );
                onUpdateGebrek(index, {
                    ...gebrek,
                    gevaarlijkesituatie: updatedSituaties,
                });
                setShowSignatureModal(false);
            } else {
                throw new Error(
                    response?.message || "De API gaf een onverwachte response."
                );
            }
        } catch (error) {
            setSignatureError(error.message);
            Swal.fire({
                icon: "error",
                title: "Melden Mislukt",
                text: `Er is een fout opgetreden: ${error.message}`,
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="border-t pt-4 mt-4">
            <div className="flex justify-between items-center mb-2">
                <h3 className="text-sm font-semibold text-red-800">
                    Gevaarlijke Situatie(s):
                </h3>
                {!hasReportedSituation && (
                    <button
                        type="button"
                        onClick={addGevaarlijkeSituatieInternal}
                        className="px-3 py-1 bg-yellow-500 text-white text-sm font-semibold rounded-md shadow-sm hover:bg-yellow-600"
                    >
                        <AddIcon /> Melden
                    </button>
                )}
                {hasReportedSituation && (
                    <span className="text-sm text-red-700 font-semibold bg-red-100 px-2 py-1 rounded">
                        Situatie Gemeld
                    </span>
                )}
            </div>

            {(!gebrek.gevaarlijkesituatie ||
                gebrek.gevaarlijkesituatie.length === 0) &&
                !hasReportedSituation && (
                    <p className="text-sm text-gray-500 italic">
                        Geen gevaarlijke situaties gemeld.
                    </p>
                )}

            <div className="space-y-3">
                {gebrek.gevaarlijkesituatie?.map((situatie, sitIndex) => {
                    const gsError = gsUploadErrors[situatie.gevaarlijkesituatieid];
                    const isReported =
                        !situatie.gevaarlijkesituatieid.startsWith("temp-");

                    return (
                        <div
                            key={situatie.gevaarlijkesituatieid}
                            className={`p-3 border rounded space-y-2 relative ${isReported
                                ? "bg-red-50 border-red-300"
                                : "bg-yellow-50 border-yellow-300"
                                }`}
                        >
                            {!isReported && (
                                <button
                                    type="button"
                                    onClick={() =>
                                        deleteGevaarlijkeSituatieInternal(
                                            situatie.gevaarlijkesituatieid
                                        )
                                    }
                                    className="absolute top-1 right-1 p-0.5 rounded hover:bg-red-100 focus:outline-none"
                                    title="Verwijder Gevaarlijke Situatie Melding"
                                >
                                    <DeleteIcon className="h-4 w-4 text-red-500 hover:text-red-700" />
                                </button>
                            )}
                            <p
                                className={`text-xs ${isReported ? "text-red-700" : "text-yellow-700"
                                    }`}
                            >
                                Datum: {situatie.datum || "Onbekend"}
                                <span className="text-gray-400 ml-2">
                                    (ID: {situatie.gevaarlijkesituatieid})
                                </span>
                            </p>
                            <div className="flex items-center gap-4">
                                {situatie.media ? (
                                    <div className="flex-shrink-0">
                                        <MediaItem
                                            mediaItem={{
                                                url: situatie.media,
                                                naam: `gs-media-${situatie.gevaarlijkesituatieid}`,
                                            }}
                                        />
                                    </div>
                                ) : (
                                    <p className="text-xs text-yellow-600 italic flex-shrink-0">
                                        Geen media.
                                    </p>
                                )}
                                {!isReported && (
                                    <div className="flex-grow">
                                        <label
                                            htmlFor={`gs-media-upload-${situatie.gevaarlijkesituatieid}`}
                                            className="sr-only"
                                        >
                                            Media toevoegen
                                        </label>
                                        <input
                                            id={`gs-media-upload-${situatie.gevaarlijkesituatieid}`}
                                            type="file"
                                            accept="image/*,video/*"
                                            onChange={(e) =>
                                                handleGevaarlijkeSituatieMediaUploadInternal(
                                                    sitIndex,
                                                    situatie.gevaarlijkesituatieid,
                                                    e
                                                )
                                            }
                                            className="block w-full text-xs text-gray-500 file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:text-xs file:font-semibold file:bg-yellow-100 file:text-yellow-700 hover:file:bg-yellow-200"
                                        />
                                        {gsError && (
                                            <p className="text-xs text-red-600 mt-1">{gsError}</p>
                                        )}
                                    </div>
                                )}
                            </div>
                            {!isReported && !situatie.media && (
                                <div className="flex-grow">
                                    <label
                                        htmlFor={`gs-media-upload-${situatie.gevaarlijkesituatieid}`}
                                        className="sr-only"
                                    >
                                        Media toevoegen
                                    </label>
                                    <input
                                        id={`gs-media-upload-${situatie.gevaarlijkesituatieid}`}
                                        type="file"
                                        accept="image/*,video/*"
                                        onChange={(e) =>
                                            handleGevaarlijkeSituatieMediaUploadInternal(
                                                sitIndex,
                                                situatie.gevaarlijkesituatieid,
                                                e
                                            )
                                        }
                                        className="block w-full text-xs text-gray-500 file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:text-xs file:font-semibold file:bg-yellow-100 file:text-yellow-700 hover:file:bg-yellow-200"
                                    />
                                    {gsError && (
                                        <p className="text-xs text-red-600 mt-1">{gsError}</p>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {/* Signature Modal */}
            <Modal open={showSignatureModal} onClose={() => setShowSignatureModal(false)}>
                <div className="p-2 max-w-lg flex flex-col space-y-2">
                    <h2 className="text-xl font-bold text-red-800 mb-4 text-center">
                        Gevaarlijke situatie melden
                    </h2>
                    <div className="mb-4 bg-gray-50 rounded-lg p-3 flex flex-col gap-1">
                        <div className="flex items-center gap-2">
                            <span className="font-semibold text-gray-700 w-28">Datum:</span>
                            <span className="text-gray-900">
                                {format(new Date(), "dd-MM-yyyy HH:mm", { locale: nl })}
                            </span>
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="font-semibold text-gray-700 w-28">Klant:</span>
                            <span className="text-gray-900">
                                {customerName || "Onbekend"}
                            </span>
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="font-semibold text-gray-700 w-28">Object:</span>
                            <span className="text-gray-900">
                                {objectName || "Onbekend"}
                            </span>
                        </div>
                    </div>
                    <p className="mb-2 text-sm text-gray-600 text-start">
                        Zet hier uw handtekening om de gevaarlijke situatie te melden.
                    </p>
                    <div className="flex justify-center mb-2">
                        <SignaturePad
                            ref={signaturePadRef}
                            canvasProps={{
                                width: 465,
                                height: 175,
                                className:
                                    "border-2 border-dashed border-gray-400 rounded-md bg-white",
                            }}
                        />
                    </div>
                    <div className="flex w-full gap-2 justify-center mb-2">
                        <button
                            type="button"
                            className="px-3 py-1 bg-gray-200 rounded hover:bg-gray-300 w-full"
                            onClick={() => signaturePadRef.current.clear()}
                        >
                            Wissen
                        </button>
                    </div>
                    <div className="flex w-full gap-2 justify-center">
                        <button
                            type="button"
                            className="px-3 py-1 bg-blue-500 rounded hover:bg-blue-600 text-white w-full"
                            onClick={() => setShowSignatureModal(false)}
                        >
                            Annuleren
                        </button>
                        <button
                            type="button"
                            className="px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 font-semibold shadow w-full"
                            onClick={handleReportWithSignature}
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? "Versturen..." : "Melden"}
                        </button>
                    </div>
                    {signatureError && (
                        <p className="text-red-600 text-xs text-center">{signatureError}</p>
                    )}
                </div>
            </Modal>
        </div>
    );
}
